import type { Panel, PanelRow, Section, SectionId } from "./panels.ts";

/**
 * Lastsummering per sektion och per panel. Använder loadWatts om satt, annars
 * räknar via ampRating · 230 V · poles (enkel uppskattning för 1-fas; 3-fas
 * antas balanserad). Meningen är att ge en snabb känsla för om panelen ligger
 * nära sin huvudskene-kapacitet, inte exakt dimensioneringsberäkning.
 */

export type LoadSummary = {
  watts: number;       // summa i W
  amps: number;        // motsvarande ström, 230 V 1-fas eller 400 V 3-fas
  rowCount: number;
};

export type PanelLoadReport = {
  /** Total last för hela panelen (alla rader). */
  panel: LoadSummary;
  /** Summa per sektion, nyckel = SectionId. */
  bySection: Map<SectionId, LoadSummary>;
  /** Osektionerade rader. */
  unsectioned: LoadSummary;
  /** True om panel.amps överstiger `busbarAmps`. */
  overBusbar: boolean;
};

const VOLTS_1P = 230;
const VOLTS_3P = 400;

function rowLoad(row: PanelRow): { w: number; a: number } {
  if (row.loadWatts !== undefined) {
    const w = row.loadWatts;
    const a = w / (row.poles === 3 || row.poles === 4 ? VOLTS_3P : VOLTS_1P);
    return { w, a };
  }
  if (row.loadAmps !== undefined) {
    const a = row.loadAmps;
    const w = a * (row.poles === 3 || row.poles === 4 ? VOLTS_3P : VOLTS_1P);
    return { w, a };
  }
  // Fallback: om inget load är satt, anta 0 — vi gissar inte från säkring
  // eftersom det skulle ge falskt hög summa.
  return { w: 0, a: 0 };
}

export function summarizePanelLoad(
  panel: Panel | undefined,
  sections: ReadonlyArray<Section>,
  rows: ReadonlyArray<PanelRow>,
): PanelLoadReport {
  const bySection = new Map<SectionId, LoadSummary>();
  for (const s of sections) {
    bySection.set(s.id, { watts: 0, amps: 0, rowCount: 0 });
  }
  const unsectioned: LoadSummary = { watts: 0, amps: 0, rowCount: 0 };
  const panelTotal: LoadSummary = { watts: 0, amps: 0, rowCount: 0 };

  for (const row of rows) {
    const { w, a } = rowLoad(row);
    panelTotal.watts += w;
    panelTotal.amps += a;
    panelTotal.rowCount += 1;
    if (row.sectionId && bySection.has(row.sectionId)) {
      const bucket = bySection.get(row.sectionId)!;
      bucket.watts += w;
      bucket.amps += a;
      bucket.rowCount += 1;
    } else {
      unsectioned.watts += w;
      unsectioned.amps += a;
      unsectioned.rowCount += 1;
    }
  }

  const overBusbar =
    panel?.busbarAmps !== undefined && panelTotal.amps > panel.busbarAmps;

  return {
    panel: panelTotal,
    bySection,
    unsectioned,
    overBusbar,
  };
}
